import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GraduationCap, Users, BookOpen, Sparkles, LogOut } from "lucide-react";
import SovereignAnchor from "../components/SovereignAnchor";

function Stat({ label, value }) {
    return (
        <div className="glass rounded-xl p-5 border border-[hsl(40_40%_25%/0.25)]">
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{label}</div>
            <div className="font-heading text-3xl mt-2 tracking-tight gold-gradient-text">{value}</div>
        </div>
    );
}

export default function TdCmdr() {
    const { logout } = useAuth();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get("/td-cmdr/dashboard");
                setData(res.data);
            } catch (e) {
                setErr(e.response?.data?.detail || "Unable to load T&D Bridge");
            }
        })();
    }, []);

    if (err) {
        return (
            <div className="min-h-screen bg-background text-foreground p-10">
                <div className="glass rounded-xl p-6 max-w-lg" data-testid="td-cmdr-error">
                    <div className="text-sm text-[hsl(350_80%_70%)]">{err}</div>
                </div>
            </div>
        );
    }

    if (!data) {
        return <div className="min-h-screen bg-background text-muted-foreground p-10">Arming the T&D Bridge...</div>;
    }

    return (
        <div className="min-h-screen bg-background text-foreground grain-overlay relative" data-testid="td-cmdr-view">
            <div className="absolute inset-0 pointer-events-none opacity-40">
                <div className="absolute top-[-20%] right-[-10%] w-[55%] h-[55%] rounded-full bg-[hsl(40_60%_45%/0.10)] blur-[120px]" />
                <div className="absolute bottom-[-20%] left-[-10%] w-[55%] h-[55%] rounded-full bg-[hsl(153_50%_25%/0.22)] blur-[120px]" />
            </div>

            <header className="relative border-b border-[hsl(40_40%_25%/0.25)] bg-[hsl(151_55%_7%/0.7)] backdrop-blur-md">
                <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                        <SovereignAnchor size={26} strokeWidth={6} className="text-[hsl(45_85%_68%)]" />
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.4em] text-muted-foreground">Training &amp; Development Bridge</div>
                            <div className="font-heading text-2xl gold-gradient-text tracking-tight">EIGOO · T&amp;D Command</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                        <Link to="/app/ad-kits" className="text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors" data-testid="td-cmdr-adkits-link">Ad Kits</Link>
                        <Link to="/app/scout" className="text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors" data-testid="td-cmdr-scout-link">Scout Reports</Link>
                        <button
                            onClick={logout}
                            data-testid="td-cmdr-logout"
                            className="flex items-center gap-1.5 text-muted-foreground hover:text-[hsl(350_80%_70%)] transition-colors"
                        >
                            <LogOut className="w-4 h-4" /> Disembark
                        </button>
                    </div>
                </div>
            </header>

            <main className="relative max-w-7xl mx-auto px-6 py-10 space-y-10">
                <section className="glass rounded-2xl p-8 md:p-10 border border-[hsl(40_40%_25%/0.3)]" data-testid="td-cmdr-hero">
                    <div className="text-[10px] uppercase tracking-[0.4em] text-muted-foreground">Commander</div>
                    <h1 className="font-heading text-5xl md:text-6xl mt-2 tracking-tighter" data-testid="td-cmdr-name">
                        {data.commander.name}
                    </h1>
                    <div className="mt-1 text-muted-foreground">
                        {data.commander.agency_name} · <span className="font-mono text-xs">{data.commander.email}</span>
                    </div>

                    <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Stat label="Partners Trained" value={data.stats.recruited} />
                        <Stat label="Approved Scout Reports" value={data.stats.approved_scout_reports} />
                        <Stat label="Ad Kit Library" value={data.stats.ad_kit_library} />
                    </div>

                    <div className="mt-8 rounded-lg border border-dashed border-[hsl(40_40%_25%/0.35)] p-5 text-sm text-muted-foreground" data-testid="td-cmdr-onboarding">
                        <div className="uppercase tracking-[0.25em] text-[10px] mb-2 text-[hsl(45_85%_68%)]">Your Charter</div>
                        <ul className="space-y-1.5 list-disc list-inside">
                            <li>Publish training playbooks &amp; Ad Kits for every sector</li>
                            <li>Host boarding ceremonies for new Silent Partners</li>
                            <li>Audit scout-report quality &amp; feed approvals to the Director</li>
                        </ul>
                    </div>
                </section>

                <section className="grid md:grid-cols-2 gap-6">
                    <div className="glass rounded-2xl p-6 border border-[hsl(40_40%_25%/0.25)]">
                        <div className="flex items-center gap-2 mb-4">
                            <Users className="w-5 h-5 text-[hsl(45_85%_68%)]" />
                            <div className="font-heading text-xl tracking-tight">Recent Recruits</div>
                        </div>
                        {data.recent_recruits?.length ? (
                            <ul className="space-y-3" data-testid="td-cmdr-recruits">
                                {data.recent_recruits.map((r) => (
                                    <li key={r.id} className="flex items-center justify-between text-sm border-b border-[hsl(40_40%_25%/0.15)] pb-2">
                                        <div>
                                            <div>{r.name || r.email}</div>
                                            <div className="text-[11px] text-muted-foreground">{r.agency_name || r.email}</div>
                                        </div>
                                        <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.2em] border border-[hsl(40_40%_35%/0.4)] text-[hsl(45_85%_68%)]">{r.role}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="text-sm text-muted-foreground">No recruits yet. Share your referral code <span className="font-mono text-[hsl(45_85%_68%)]">{data.commander.referral_code}</span> to begin training.</div>
                        )}
                    </div>

                    <div className="glass rounded-2xl p-6 border border-[hsl(40_40%_25%/0.25)]">
                        <div className="flex items-center gap-2 mb-4">
                            <BookOpen className="w-5 h-5 text-[hsl(45_85%_68%)]" />
                            <div className="font-heading text-xl tracking-tight">Training Library Snapshot</div>
                        </div>
                        {data.training_kits?.length ? (
                            <div className="grid grid-cols-2 gap-3" data-testid="td-cmdr-kits">
                                {data.training_kits.map((k) => (
                                    <div key={k.id} className="rounded-lg border border-[hsl(40_40%_25%/0.3)] p-3 bg-[hsl(151_55%_9%)]">
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(45_85%_68%)]">{k.sector}</div>
                                        <div className="text-sm mt-1 line-clamp-2">{k.title}</div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-muted-foreground">The library is empty. Seed your first kit in the Ad Kit Repository.</div>
                        )}
                        <Link to="/app/ad-kits" className="inline-flex items-center gap-1 mt-4 text-xs text-[hsl(45_85%_68%)] hover:underline" data-testid="td-cmdr-library-link">
                            <Sparkles className="w-3 h-3" /> Open full Ad Kit repository
                        </Link>
                    </div>
                </section>
            </main>
        </div>
    );
}
